import { useRef, useState } from "react";
import { Button } from "./Button";
import { Dialog } from "./Dialog";

type DeleteRequest = {
  name: string;
  description: string;
  onConfirm: () => Promise<unknown>;
};

function DeleteConfirmation({ request, onClose }: { request: DeleteRequest; onClose: () => void }) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const inFlight = useRef(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  const confirm = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setError(false);
    try {
      await request.onConfirm();
      onClose();
    } catch {
      setError(true);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  return (
    <Dialog.Root
      open
      disablePointerDismissal
      onOpenChange={(open) => {
        if (!open && !inFlight.current) onClose();
      }}
    >
      <Dialog.Content
        initialFocus={cancelRef}
        showClose={!pending}
        className="w-[calc(100%-2rem)]"
        aria-busy={pending}
      >
        <Dialog.Header>
          <Dialog.Title className="break-words">Move “{request.name}” to Trash?</Dialog.Title>
          <Dialog.Description>{request.description}</Dialog.Description>
        </Dialog.Header>
        {error && (
          <p role="alert" className="text-sm text-danger">
            Could not move this item to Trash. Please try again.
          </p>
        )}
        <Dialog.Footer>
          <Button ref={cancelRef} variant="secondary" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={pending} onClick={() => void confirm()}>
            {pending ? "Moving…" : "Move to Trash"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/** Defers deletion until explicitly confirmed; render `deleteDialog` once beside the caller. */
export function useDeleteConfirmation() {
  const nextId = useRef(0);
  const [request, setRequest] = useState<(DeleteRequest & { id: number }) | null>(null);
  return {
    requestDelete: (next: DeleteRequest) => setRequest({ ...next, id: nextId.current++ }),
    deleteDialog: request ? (
      <DeleteConfirmation
        key={request.id}
        request={request}
        onClose={() => setRequest((current) => (current?.id === request.id ? null : current))}
      />
    ) : null,
  };
}
